import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  ArrowLeft, 
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  DollarSign,
  CreditCard,
  Calendar,
  User,
  ExternalLink,
  Copy,
  Receipt
} from 'lucide-react';
import { 
  Chip,
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Button as MuiButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Alert
} from '@mui/material';
import adminApi from '../../api/admin';
import Button from '../../components/common/Button';
import { toast } from 'react-toastify';
import { formatDateVerbose } from '../../utils/helpers';

const AdminPaymentDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [refreshDialogOpen, setRefreshDialogOpen] = useState(false);

  const {
    data: payment,
    isLoading,
    isError,
    error,
    refetch
  } = useQuery({
    queryKey: ["payment-details", id],
    queryFn: async () => {
      const res = await adminApi.getPaymentDetails(id);
      return res.data;
    },
    enabled: !!id,
  });

  // Refresh payment status mutation
  const refreshPaymentMutation = useMutation({
    mutationFn: () => adminApi.getPaymentDetails(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-details', id] });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      setRefreshDialogOpen(false);
      toast.success('Payment status refreshed successfully!');
    },
    onError: (error) => {
      console.error('Failed to refresh payment:', error);
      toast.error('Failed to refresh payment status');
    }
  });

  const handleRefresh = () => {
    setRefreshDialogOpen(true);
  };

  const confirmRefresh = () => {
    refreshPaymentMutation.mutate();
    // refetch()
    // setRefreshDialogOpen(false);
  };

  const handleBack = () => {
    navigate('/admin/subscription-payments');
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const formatGatewayResponse = (response) => {
    if (!response) return null;
    
    try {
      return JSON.stringify(response, null, 2);
    } catch (error) {
      return String(response);
    }
  };

  const getStatusConfig = (status) => {
    const statusConfig = {
      completed: { color: 'success', label: 'Completed', icon: CheckCircle, description: 'Payment was successfully processed' },
      pending: { color: 'warning', label: 'Pending', icon: Clock, description: 'Payment is being processed' },
      failed: { color: 'error', label: 'Failed', icon: XCircle, description: 'Payment failed to process' },
      refunded: { color: 'info', label: 'Refunded', icon: RefreshCw, description: 'Payment has been refunded' },
      canceled: { color: 'default', label: 'Canceled', icon: XCircle, description: 'Payment was canceled' }
    };
    
    return statusConfig[status] || { color: 'default', label: status, icon: AlertCircle, description: 'Unknown status' };
  };

  const getGatewayConfig = (gateway) => {
    const gatewayConfig = {
      stripe: { color: 'primary', label: 'Stripe' },
      daraja: { color: 'secondary', label: 'Daraja' },
      paypal: { color: 'info', label: 'PayPal' }
    };
    
    return gatewayConfig[gateway] || { color: 'default', label: gateway };
  };

  const extractDarajaPaymentDetails = (gatewayResponse) => {
    if (!gatewayResponse) return null;
    
    try {
      // Handle different response formats
      if (gatewayResponse.Body?.stkCallback) {
        const callback = gatewayResponse.Body.stkCallback;
        const metadata = callback.CallbackMetadata?.Item || [];
        
        const details = {};
        metadata.forEach(item => {
          if (item.Name && item.Value !== undefined) {
            details[item.Name] = item.Value;
          }
        });
        
        return {
          resultCode: callback.ResultCode,
          resultDesc: callback.ResultDesc,
          receiptNumber: details.MpesaReceiptNumber,
          phoneNumber: details.PhoneNumber,
          transactionDate: details.TransactionDate,
          amount: details.Amount,
          checkoutRequestID: callback.CheckoutRequestID,
          merchantRequestID: callback.MerchantRequestID
        };
      } else if (gatewayResponse.ResponseCode) {
        // Initial response format
        return {
          responseCode: gatewayResponse.ResponseCode,
          customerMessage: gatewayResponse.CustomerMessage,
          responseDescription: gatewayResponse.ResponseDescription,
          checkoutRequestID: gatewayResponse.CheckoutRequestID,
          merchantRequestID: gatewayResponse.MerchantRequestID
        };
      }
    } catch (error) {
      console.error('Error parsing gateway response:', error);
    }
    
    return null;
  };

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto py-8 px-4">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-48 mb-6"></div>
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="h-8 bg-gray-200 rounded w-2/3 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-8"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-20 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="max-w-6xl mx-auto py-8 px-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error loading payment details: {error?.message}</p>
          <Button 
            onClick={handleBack}
            variant="outline" 
            className="mt-4"
            icon={<ArrowLeft className="w-4 h-4 mr-2" />}
          >
            Back to Payments
          </Button>
        </div>
      </div>
    );
  }

  if (!payment) {
    return (
      <div className="max-w-6xl mx-auto py-8 px-4">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">Payment not found</p>
          <Button 
            onClick={handleBack}
            variant="outline" 
            className="mt-4"
            icon={<ArrowLeft className="w-4 h-4 mr-2" />}
          >
            Back to Payments
          </Button>
        </div>
      </div>
    );
  }

  const statusConfig = getStatusConfig(payment.status);
  const gatewayConfig = getGatewayConfig(payment.gateway);
  const darajaDetails = payment.gateway === 'daraja' ? extractDarajaPaymentDetails(payment.gateway_response) : null;

  return (
    <div className="max-w-6xl mx-auto py-8 px-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            onClick={handleBack}
            icon={<ArrowLeft className="w-4 h-4" />}
            className="p-2"
          />
          <div>
            <h1 className="text-3xl font-bold text-secondary-900">Payment Details</h1>
            <p className="text-secondary-600">Transaction ID: {payment.transaction_id}</p>
          </div>
        </div>
        <div className="flex space-x-3">
          <Button
            variant="outline"
            onClick={handleRefresh}
            icon={<RefreshCw className="w-4 h-4 mr-2" />}
            disabled={refreshPaymentMutation.isLoading}
          >
            Refresh Status
          </Button>
        </div>
      </div>

      {/* Payment Overview */}
      <Grid container spacing={3} className="mb-6">
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent className="p-6">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between mb-6">
                <div className="flex items-center space-x-4 mb-4 lg:mb-0">
                  <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-blue-600 rounded-full flex items-center justify-center">
                    <CreditCard className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <div className="flex items-center space-x-3 mb-2">
                      <h2 className="text-2xl font-bold text-secondary-900">
                        {payment.amount} {payment.currency}
                      </h2>
                      <Chip 
                        label={statusConfig.label} 
                        color={statusConfig.color}
                        icon={<statusConfig.icon className="w-4 h-4" />}
                      />
                    </div>
                    <p className="text-secondary-600">{statusConfig.description}</p>
                  </div>
                </div>
                
                <div className="bg-gradient-to-r from-blue-50 to-green-50 p-4 rounded-lg">
                  <div className="flex items-center space-x-2 mb-2">
                    <Chip 
                      label={gatewayConfig.label}
                      color={gatewayConfig.color}
                      variant="outlined"
                    />
                  </div>
                  <div className="text-sm text-secondary-600">
                    Payment Gateway
                  </div>
                </div>
              </div>

              {/* Transaction Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 border-t pt-6">
                <div>
                  <h3 className="text-lg font-semibold mb-4 text-secondary-900">Transaction Information</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-secondary-600">Transaction ID</span>
                      <div className="flex items-center space-x-2">
                        <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                          {payment.transaction_id}
                        </code>
                        <MuiButton 
                          size="small" 
                          onClick={() => copyToClipboard(payment.transaction_id)}
                          startIcon={<Copy className="w-3 h-3" />}
                        >
                          Copy
                        </MuiButton>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-secondary-600">Subscription ID</span>
                      <Link to={`/admin/subscriptions/${payment.subscription}`} className='text-blue-600 hover:underline font-medium'>{payment.subscription}</Link>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-secondary-600">Payment Gateway</span>
                      <Chip 
                        label={gatewayConfig.label}
                        color={gatewayConfig.color}
                        size="small"
                        variant="outlined"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-4 text-secondary-900">Timeline</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-secondary-600">Created</span>
                      <span className="font-medium text-sm">
                        {formatDateVerbose(payment.created_at)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-secondary-600">Last Updated</span>
                      <span className="font-medium text-sm">
                        {formatDateVerbose(payment.updated_at)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Card>
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold mb-4 text-secondary-900">Quick Actions</h3>
              <div className="space-y-3">
                <Button
                  fullWidth
                  variant="outline"
                  onClick={handleRefresh}
                  loading={refreshPaymentMutation.isLoading}
                  icon={<RefreshCw className="w-4 h-4 mr-2" />}
                >
                  Refresh Status
                </Button>
                {payment.subscription && (
                  <Button
                    fullWidth
                    variant="outline"
                    onClick={() => navigate(`/admin/subscriptions/${payment.subscription}`)}
                    icon={<ExternalLink className="w-4 h-4 mr-2" />}
                  >
                    View Subscription
                  </Button>
                )}
              </div>

              {/* Status Information */}
              <Divider className="my-4" />
              <div className="space-y-3">
                <h4 className="font-semibold text-secondary-900">Status Information</h4>
                <Alert severity={statusConfig.color} className="text-sm">
                  {statusConfig.description}
                </Alert>
                {payment.status === 'pending' && (
                  <Typography variant="body2" className="text-yellow-600">
                    This payment is still being processed. You can refresh the status to get the latest update.
                  </Typography>
                )}
                {payment.status === 'failed' && (
                  <Typography variant="body2" className="text-red-600">
                    This payment failed to process. Check the gateway response for more details.
                  </Typography>
                )}
              </div>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Gateway Specific Details */}
      {darajaDetails && (
        <Card className="mb-6">
          <CardContent className="p-6">
            <h3 className="text-xl font-semibold mb-4 text-secondary-900 flex items-center">
              <Receipt className="w-5 h-5 mr-2" />
              Daraja Payment Details
            </h3>
            <Grid container spacing={3}>
              {darajaDetails.receiptNumber && (
                <Grid item xs={12} sm={6} md={3}>
                  <div className="text-center p-3 border rounded-lg">
                    <div className="text-sm text-secondary-600">Receipt Number</div>
                    <div className="font-bold text-lg">{darajaDetails.receiptNumber}</div>
                  </div>
                </Grid>
              )}
              {darajaDetails.phoneNumber && (
                <Grid item xs={12} sm={6} md={3}>
                  <div className="text-center p-3 border rounded-lg">
                    <div className="text-sm text-secondary-600">Phone Number</div>
                    <div className="font-bold text-lg">{darajaDetails.phoneNumber}</div>
                  </div>
                </Grid>
              )}
              {darajaDetails.amount && (
                <Grid item xs={12} sm={6} md={3}>
                  <div className="text-center p-3 border rounded-lg">
                    <div className="text-sm text-secondary-600">Amount</div>
                    <div className="font-bold text-lg">{darajaDetails.amount}</div>
                  </div>
                </Grid>
              )}
              {darajaDetails.transactionDate && (
                <Grid item xs={12} sm={6} md={3}>
                  <div className="text-center p-3 border rounded-lg">
                    <div className="text-sm text-secondary-600">Transaction Date</div>
                    <div className="font-bold text-lg">{formatDateVerbose(darajaDetails.transactionDate)}</div>
                  </div>
                </Grid>
              )}
            </Grid>
            {darajaDetails.resultDesc && (
              <Alert severity={darajaDetails.resultCode === 0 ? 'success' : 'error'} className="mt-4">
                {darajaDetails.resultDesc}
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Raw Gateway Response */}
      <Card>
        <CardContent className="p-6">
          <h3 className="text-xl font-semibold mb-4 text-secondary-900">Gateway Response</h3>
          {payment.gateway_response ? (
            <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm overflow-x-auto">
              <pre>{formatGatewayResponse(payment.gateway_response)}</pre>
            </div>
          ) : (
            <div className="text-center py-8 text-secondary-500">
              <AlertCircle className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p>No gateway response available</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Refresh Confirmation Dialog */}
      <Dialog open={refreshDialogOpen} onClose={() => setRefreshDialogOpen(false)}>
        <DialogTitle className="flex items-center space-x-2">
          <RefreshCw className="w-5 h-5 text-blue-500" />
          <span>Refresh Payment Status</span>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to refresh the status of this payment?
          </Typography>
          <Typography variant="body2" className="mt-2 text-gray-600">
            This will query the payment gateway for the latest status update.
          </Typography>
        </DialogContent>
        <DialogActions>
          <MuiButton 
            onClick={() => setRefreshDialogOpen(false)}
            disabled={refreshPaymentMutation.isLoading}
          >
            Cancel
          </MuiButton>
          <MuiButton 
            onClick={confirmRefresh}
            variant="contained"
            color="primary"
            disabled={refreshPaymentMutation.isLoading}
            startIcon={refreshPaymentMutation.isLoading ? null : <RefreshCw className="w-4 h-4" />}
          >
            {refreshPaymentMutation.isLoading ? 'Refreshing...' : 'Refresh Status'}
          </MuiButton>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default AdminPaymentDetailsPage;